/**
 * DiscoverySummaryCard - Minimal view showing essential extracted info
 * Part of progressive disclosure pattern - shows key facts with status indicators
 */

import {
  Check,
  AlertTriangle,
  Building2,
  Fingerprint,
  MapPin,
  Landmark,
  Users,
  UserCheck,
  ChevronDown,
  Info,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
  ExtractedCompanyProfile,
  DataConflict,
} from "@shared/discovery-types";

interface DiscoverySummaryCardProps {
  profile: ExtractedCompanyProfile;
  conflicts: DataConflict[];
  onExpandAll?: () => void;
  className?: string;
}

interface StatusItemProps {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
  status: "confirmed" | "attention" | "missing";
  tooltip?: string;
}

function StatusItem({ icon, label, value, status, tooltip }: StatusItemProps) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div
        className={cn(
          "flex items-center justify-center h-8 w-8 rounded-full",
          status === "confirmed" && "bg-success/10 text-success",
          status === "attention" && "bg-warning/10 text-warning",
          status === "missing" && "bg-muted text-muted-foreground"
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div
          className={cn(
            "text-sm font-medium truncate",
            !value && "text-muted-foreground italic"
          )}
        >
          {value || "Not extracted"}
        </div>
      </div>
      <div className="shrink-0">
        {status === "confirmed" && <Check className="h-4 w-4 text-success" />}
        {status === "attention" && (
          <AlertTriangle className="h-4 w-4 text-warning" />
        )}
        {status === "missing" && (
          <Info className="h-4 w-4 text-muted-foreground/50" />
        )}
      </div>
    </div>
  );
}

interface CountBadgeProps {
  icon: React.ReactNode;
  count: number;
  label: string;
  hasAttention?: boolean;
}

function CountBadge({ icon, count, label, hasAttention }: CountBadgeProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-lg border",
        hasAttention ? "border-[var(--warning)]/30 bg-warning/5" : "bg-muted/30"
      )}
    >
      <span className="text-muted-foreground">{icon}</span>
      <div>
        <div className="text-lg font-semibold leading-none">{count}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
      {hasAttention && (
        <AlertTriangle className="h-3.5 w-3.5 text-warning ml-1" />
      )}
    </div>
  );
}

export function DiscoverySummaryCard({
  profile,
  conflicts,
  onExpandAll,
  className,
}: DiscoverySummaryCardProps) {
  const { companyInfo, taxInfo, bankAccounts, vendors, customers } = profile;

  // Determine status for each key field
  const getFieldStatus = (
    value: string | undefined | null,
    fieldPath: string,
    confidence?: number
  ): "confirmed" | "attention" | "missing" => {
    if (!value) return "missing";
    const hasConflict = conflicts.some(c => c.field === fieldPath);
    if (hasConflict) return "attention";
    if (confidence !== undefined && confidence < 70) return "attention";
    return "confirmed";
  };

  const nameStatus = getFieldStatus(
    companyInfo?.name,
    "companyInfo.name",
    companyInfo?.confidence
  );
  const uidStatus = getFieldStatus(companyInfo?.uid, "companyInfo.uid");
  const cantonStatus = getFieldStatus(
    taxInfo?.canton || companyInfo?.address?.canton,
    "taxInfo.canton"
  );

  // Count items needing attention in each section
  const bankAccountsWithLowConfidence =
    bankAccounts?.filter(a => a.confidence < 70).length || 0;
  const vendorsWithLowConfidence =
    vendors?.filter(v => v.confidence < 70).length || 0;
  const customersWithLowConfidence =
    customers?.filter(c => c.confidence < 70).length || 0;

  // Calculate overall status
  const confirmedCount = [nameStatus, uidStatus, cantonStatus].filter(
    s => s === "confirmed"
  ).length;
  const totalKeyFields = 3;

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-0">
        {/* Header with overall status */}
        <div className="p-4 border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "h-10 w-10 rounded-full flex items-center justify-center",
                  confirmedCount === totalKeyFields
                    ? "bg-success/10"
                    : confirmedCount > 0
                      ? "bg-warning/10"
                      : "bg-muted"
                )}
              >
                <Building2
                  className={cn(
                    "h-5 w-5",
                    confirmedCount === totalKeyFields
                      ? "text-success"
                      : confirmedCount > 0
                        ? "text-warning"
                        : "text-muted-foreground"
                  )}
                />
              </div>
              <div>
                <h3 className="font-semibold">
                  {companyInfo?.name || "Company Profile"}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {confirmedCount}/{totalKeyFields} key fields confirmed
                </p>
              </div>
            </div>
            {conflicts.length > 0 && (
              <Badge
                variant="outline"
                className="text-warning border-[var(--warning)]/30"
              >
                {conflicts.length} conflict{conflicts.length !== 1 ? "s" : ""}{" "}
                to resolve
              </Badge>
            )}
          </div>
        </div>

        {/* Key fields */}
        <div className="p-4 space-y-1">
          <StatusItem
            icon={<Building2 className="h-4 w-4" />}
            label="Company Name"
            value={companyInfo?.name}
            status={nameStatus}
          />
          <StatusItem
            icon={<Fingerprint className="h-4 w-4" />}
            label="Swiss UID"
            value={companyInfo?.uid}
            status={uidStatus}
          />
          <StatusItem
            icon={<MapPin className="h-4 w-4" />}
            label="Canton"
            value={taxInfo?.canton || companyInfo?.address?.canton}
            status={cantonStatus}
          />
        </div>

        {/* Counts */}
        <div className="px-4 pb-4">
          <div className="grid grid-cols-3 gap-2">
            <CountBadge
              icon={<Landmark className="h-4 w-4" />}
              count={bankAccounts?.length || 0}
              label="Accounts"
              hasAttention={bankAccountsWithLowConfidence > 0}
            />
            <CountBadge
              icon={<Users className="h-4 w-4" />}
              count={vendors?.length || 0}
              label="Vendors"
              hasAttention={vendorsWithLowConfidence > 0}
            />
            <CountBadge
              icon={<UserCheck className="h-4 w-4" />}
              count={customers?.length || 0}
              label="Customers"
              hasAttention={customersWithLowConfidence > 0}
            />
          </div>
        </div>

        {/* Expand all button */}
        {onExpandAll && (
          <div className="px-4 pb-4 pt-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onExpandAll}
              className="w-full text-muted-foreground hover:text-foreground"
            >
              <ChevronDown className="h-4 w-4 mr-2" />
              Review all details
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
